import { useEffect } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { authRepository } from '@/data/adapters/api/auth.api'
import { useSessionStore, selectAudience } from '@/lib/session.store'
import { ApiError } from '@scc/lib'

const PUBLIC_PATH_PREFIXES = ['/login', '/register', '/invitations/accept', '/p', '/l', '/portal']

function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

/**
 * On mount, validate the persisted session and subscribe to mid-session
 * auth-state changes so a token clear (from /me, /refresh, or any 401)
 * forces the user back to /login.
 */
export function useSessionBootstrap(): void {
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  // One-shot bootstrap on mount.
  useEffect(() => {
    let cancelled = false

    async function bootstrap() {
      const { token, setSessionStatus, setSession, clearSession } = useSessionStore.getState()
      setSessionStatus('loading')

      try {
        if (token) {
          // Client (portal) tokens are NOT valid at the staff `/auth/me`
          // endpoint — validating them there 401s and would wrongly clear the
          // session on every load (the "portal refresh logs you out" bug).
          // Trust the persisted client token; portal API calls refresh via the
          // shared `cc_refresh` cookie (and guards redirect) if it's stale.
          if (selectAudience({ token }) === 'client') {
            setSession(token)
            return
          }
          const user = await authRepository.me()
          if (cancelled) return
          setSession(useSessionStore.getState().token ?? token, user)
          return
        }

        const result = await authRepository.refresh()
        if (cancelled) return
        setSession(result.token, result.user)
      } catch (err) {
        if (cancelled) return
        if (err instanceof ApiError && err.status !== 0) {
          clearSession()
        } else {
          setSessionStatus('idle')
        }
      }
    }

    void bootstrap()
    return () => {
      cancelled = true
    }
  }, [])

  // Subscribe to token changes — when it transitions to null on a guarded
  // path, redirect to /login.
  useEffect(() => {
    const unsubscribe = useSessionStore.subscribe((state, prev) => {
      if (prev.token && !state.token && !isPublicPath(pathname)) {
        void navigate({ to: '/login' })
      }
    })
    return unsubscribe
  }, [navigate, pathname])
}
